import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CrewMember, RaceEvent } from '../types';
import { crewService } from '../services/crewService';
import { eventService } from '../services/eventService';

/**
 * Crew List Builder Page — allows users to build a crew list for a selected event.
 *
 * Requirements:
 *  5.1 — Display all available crew members for selection
 *  5.2 — Add crew member to event creates association
 *  5.3 — Remove crew member from event deletes association
 *  5.4 — Display all crew members currently associated with event
 *  5.5 — Persist crew-event associations to database
 */

/** Invite status badge colors */
const INVITE_COLORS: Record<string, string> = {
  invite: '#1a3a5c',
  sent: '#2196f3',
  accepted: '#4caf50',
  declined: '#f44336',
  reinvite: '#ff9800',
};

function getInviteDisplay(status?: string, sentAt?: string, hasPhone?: boolean): {
  label: string; color: string; clickable: boolean;
} {
  if (status === 'accepted') return { label: 'accepted', color: INVITE_COLORS.accepted, clickable: false };
  if (status === 'declined') return { label: 'declined', color: INVITE_COLORS.declined, clickable: false };
  if (status === 'sent') {
    if (sentAt) {
      const hours = (Date.now() - new Date(sentAt).getTime()) / 3_600_000;
      if (hours > 24) return { label: 're-invite', color: INVITE_COLORS.reinvite, clickable: true };
    }
    return { label: 'sent', color: INVITE_COLORS.sent, clickable: false };
  }
  if (hasPhone) return { label: 'invite', color: INVITE_COLORS.invite, clickable: true };
  return { label: '', color: '', clickable: false };
}

/** Crew role options for assignment. */
const CREW_ROLES = [
  { value: 'skipper', label: 'Skipper' },
  { value: 'navigator', label: 'Navigator' },
  { value: 'watch_captain', label: 'Watch Captain' },
  { value: 'crew', label: 'Crew' },
];

/** Available crew member card for selection. */
const AvailableCrewCard: React.FC<{
  member: CrewMember;
  onAdd: (crewId: string, role: string) => void;
  adding: boolean;
}> = ({ member, onAdd, adding }) => {
  const [selectedRole, setSelectedRole] = useState('crew');

  const handleAdd = () => {
    onAdd(member.crewId, selectedRole);
  };

  return (
    <div className="available-crew-card" data-testid={`available-crew-${member.crewId}`}>
      <div className="crew-info">
        <span className="crew-name">{member.firstName} {member.lastName}</span>
        <span className="crew-as">{member.asNumber}</span>
        <span className={`verification-badge ${member.asVerified ? 'verified' : 'unverified'}`}>
          {member.asVerified ? '✓' : '⏳'}
        </span>
      </div>
      <div className="crew-certs">
        {member.certificates.length > 0 && (
          <span className="cert-count">{member.certificates.length} cert(s)</span>
        )}
      </div>
      <div className="crew-actions">
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          data-testid={`role-select-${member.crewId}`}
        >
          {CREW_ROLES.map(role => (
            <option key={role.value} value={role.value}>{role.label}</option>
          ))}
        </select>
        <button
          onClick={handleAdd}
          disabled={adding}
          className="btn-add"
          data-testid={`add-crew-${member.crewId}`}
        >
          {adding ? 'Adding...' : 'Add'}
        </button>
      </div>
    </div>
  );
};

/** Selected crew member card with remove and invite options. */
const SelectedCrewCard: React.FC<{
  member: CrewMember;
  role?: string;
  onRemove: (crewId: string) => void;
  onInvite?: (crewId: string) => void;
  removing: boolean;
  inviting?: boolean;
}> = ({ member, role, onRemove, onInvite, removing, inviting }) => {
  const invite = getInviteDisplay(member.inviteStatus, member.inviteSentAt, !!member.phoneNumber);

  return (
    <div className="selected-crew-card" data-testid={`selected-crew-${member.crewId}`}>
      <div className="crew-info">
        <span className="crew-name">{member.firstName} {member.lastName}</span>
        {invite.label && (
          invite.clickable && onInvite ? (
            <button
              type="button"
              onClick={() => onInvite(member.crewId)}
              disabled={inviting}
              style={{
                backgroundColor: invite.color,
                color: '#fff',
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: '10px',
                marginLeft: '8px',
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              title={invite.label === 'invite' ? 'Send WhatsApp invite' : 'Re-send WhatsApp invite'}
            >
              {inviting ? 'Sending...' : invite.label}
            </button>
          ) : (
            <span
              style={{
                backgroundColor: invite.color,
                color: '#fff',
                fontSize: '0.7rem',
                padding: '2px 8px',
                borderRadius: '10px',
                marginLeft: '8px',
                whiteSpace: 'nowrap',
              }}
            >
              {invite.label}
            </span>
          )
        )}
        <span className="crew-role">{CREW_ROLES.find(r => r.value === role)?.label || role || 'Crew'}</span>
      </div>
      <div className="crew-details">
        <span className="crew-as">{member.asNumber}</span>
        <span className={`verification-badge ${member.asVerified ? 'verified' : 'unverified'}`}>
          {member.asVerified ? '✓ Verified' : '⏳ Pending'}
        </span>
      </div>
      <div className="crew-certs">
        {member.certificates.map(cert => (
          <span key={cert.certificateId} className="cert-badge" title={cert.name}>
            {cert.type}
          </span>
        ))}
      </div>
      <button
        onClick={() => onRemove(member.crewId)}
        disabled={removing}
        className="btn-remove"
        data-testid={`remove-crew-${member.crewId}`}
      >
        {removing ? 'Removing...' : 'Remove'}
      </button>
    </div>
  );
};

const CrewListBuilderPage: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<RaceEvent | null>(null);
  const [allCrew, setAllCrew] = useState<CrewMember[]>([]);
  const [eventCrew, setEventCrew] = useState<CrewMember[]>([]);
  const [crewRoles, setCrewRoles] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingCrewId, setAddingCrewId] = useState<string | null>(null);
  const [removingCrewId, setRemovingCrewId] = useState<string | null>(null);
  const [invitingCrewId, setInvitingCrewId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!raceId) return;
    
    setLoading(true);
    setError(null);
    try {
      // Fetch all data in parallel
      const [events, userCrew, raceEventCrew] = await Promise.all([
        eventService.getEvents(),
        crewService.getCrew(),
        eventService.getEventCrew(raceId)
      ]);
      
      // Find the specific event
      const currentEvent = events.find(e => e.raceId === raceId);
      setEvent(currentEvent || null);
      setAllCrew(userCrew);
      setEventCrew(raceEventCrew);
    } catch {
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [raceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll event crew every 30 seconds for invite status updates
  useEffect(() => {
    if (!raceId) return;
    const interval = setInterval(async () => {
      try {
        const updatedCrew = await eventService.getEventCrew(raceId);
        setEventCrew(updatedCrew);
      } catch { /* ignore polling errors */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [raceId]);

  // Filter available crew (not already in event)
  const availableCrew = allCrew.filter(
    member => !eventCrew.some(ec => ec.crewId === member.crewId)
  );

  const handleAddCrew = async (crewId: string, role: string) => {
    if (!raceId) return;
    
    setAddingCrewId(crewId);
    try {
      await eventService.addCrewToEvent(raceId, { crewId, role });
      // Add to local state
      const addedMember = allCrew.find(m => m.crewId === crewId);
      if (addedMember) {
        setEventCrew([...eventCrew, addedMember]);
        setCrewRoles({ ...crewRoles, [crewId]: role });
      }
    } catch {
      alert('Failed to add crew member to event. Please try again.');
    } finally {
      setAddingCrewId(null);
    }
  };

  const handleRemoveCrew = async (crewId: string) => {
    if (!raceId) return;
    
    setRemovingCrewId(crewId);
    try {
      await eventService.removeCrewFromEvent(raceId, crewId);
      // Remove from local state
      setEventCrew(eventCrew.filter(m => m.crewId !== crewId));
      const newRoles = { ...crewRoles };
      delete newRoles[crewId];
      setCrewRoles(newRoles);
    } catch {
      alert('Failed to remove crew member from event. Please try again.');
    } finally {
      setRemovingCrewId(null);
    }
  };

  const handleInviteCrew = async (crewId: string) => {
    if (!raceId) return;

    setInvitingCrewId(crewId);
    try {
      await eventService.sendInvite(raceId, crewId);
      // Update local state to reflect sent status
      setEventCrew(prev => prev.map(m =>
        m.crewId === crewId ? { ...m, inviteStatus: 'sent' } : m
      ));
    } catch {
      alert('Failed to send invite. Check WhatsApp settings in admin portal.');
    } finally {
      setInvitingCrewId(null);
    }
  };

  const handleCheckCompliance = () => {
    if (raceId) {
      navigate(`/events/${raceId}/compliance`);
    }
  };

  const handleBack = () => {
    navigate('/race-events');
  };

  if (loading) {
    return (
      <div className="crew-list-builder-page">
        <div className="loading-state" data-testid="builder-loading">
          <div className="loading-spinner"></div>
          <p>Loading crew list builder...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="crew-list-builder-page">
        <div className="error-state" data-testid="event-not-found">
          <h2>Event Not Found</h2>
          <p>The requested race event could not be found.</p>
          <button onClick={handleBack} className="btn-back">
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="crew-list-builder-page">
      <div className="page-header">
        <button onClick={handleBack} className="btn-back" data-testid="back-btn">
          ← Back to Events
        </button>
        <div className="event-info">
          <h1>Build Crew List</h1>
          <h2 data-testid="event-title">{event.name}</h2>
          <span className="event-category">{event.category}</span>
        </div>
      </div>

      {error && (
        <div className="error-banner" data-testid="builder-error">
          <p>{error}</p>
          <button onClick={fetchData} className="btn-retry">Retry</button>
        </div>
      )}

      <div className="builder-content">
        {/* Selected Crew Section */}
        <section className="selected-crew-section">
          <div className="section-header">
            <h3>Event Crew ({eventCrew.length})</h3>
            {eventCrew.length > 0 && (
              <button
                onClick={handleCheckCompliance}
                className="btn-compliance"
                data-testid="check-compliance-btn"
              >
                Check Compliance →
              </button>
            )}
          </div>
          
          {eventCrew.length === 0 ? (
            <div className="empty-crew" data-testid="event-crew-empty">
              <p>No crew members added yet. Select crew members from the available list below.</p>
            </div>
          ) : (
            <div className="selected-crew-list" data-testid="event-crew-list">
              {eventCrew.map(member => (
                <SelectedCrewCard
                  key={member.crewId}
                  member={member}
                  role={crewRoles[member.crewId]}
                  onRemove={handleRemoveCrew}
                  onInvite={handleInviteCrew}
                  removing={removingCrewId === member.crewId}
                  inviting={invitingCrewId === member.crewId}
                />
              ))}
            </div>
          )}
        </section>

        {/* Available Crew Section */}
        <section className="available-crew-section">
          <h3>Available Crew ({availableCrew.length})</h3>
          
          {availableCrew.length === 0 ? (
            <div className="empty-available" data-testid="available-crew-empty">
              {allCrew.length === 0 ? (
                <p>No crew members found. <a href="/crew">Add crew members</a> first.</p>
              ) : (
                <p>All crew members have been added to this event.</p>
              )}
            </div>
          ) : (
            <div className="available-crew-list" data-testid="available-crew-list">
              {availableCrew.map(member => (
                <AvailableCrewCard
                  key={member.crewId}
                  member={member}
                  onAdd={handleAddCrew}
                  adding={addingCrewId === member.crewId}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CrewListBuilderPage;
